/**
 * TemplateBuilderPage
 * Full-screen email builder at /templates/:id/edit
 *
 * Three tabs:
 *  1. Visual  — Unlayer drag-&-drop editor (react-email-editor)
 *  2. HTML    — Monaco Editor (raw HTML source)
 *  3. Preview — sandboxed <iframe srcdoc> with live variable substitution
 *
 * Image upload: presigned S3 PUT URL via POST /api/v1/templates/upload-url
 */

import { useRef, useState, useEffect, useCallback } from 'react';

// ─── Hook: measure a div's pixel height via ResizeObserver ────────────────────
// Accepts the element directly (not a ref) so the effect re-runs when the
// element first mounts — which matters when the div is behind an early return
// during initial data loading (ref.current would be null on first effect run).
function useElementHeight(el: HTMLDivElement | null): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setHeight(entry.contentRect.height);
    });
    ro.observe(el);
    // Set initial value immediately
    setHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [el]);
  return height;
}
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import EmailEditor, { type EditorRef, type EmailEditorProps } from 'react-email-editor';
import Editor from '@monaco-editor/react';
import {
  ChevronLeft,
  Eye,
  Code2,
  LayoutTemplate,
  Save,
  CheckCircle,
  AlertTriangle,
  Variable,
  ChevronDown,
  ChevronUp,
  Loader2,
  Copy,
  Check,
  X,
  Link2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { UNLAYER_MERGE_TAGS, MERGE_TAG_SAMPLES } from '../lib/mergeTags.ts';
import { Badge } from '../components/ui/Badge.tsx';
import { Button } from '../components/ui/Button.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { cn } from '../components/ui/cn.ts';
import { TemplatePickerModal } from '../components/ui/TemplatePickerModal.tsx';
import { MediaPickerModal } from '../components/ui/MediaPickerModal.tsx';
import { ShortlinkPickerModal } from '../components/ui/ShortlinkPickerModal.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Template {
  id: string;
  name: string;
  channel: 'email' | 'sms' | 'push' | 'webhook';
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  bodyJson: Record<string, unknown> | null;
  variables: string[] | null;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

type Tab = 'visual' | 'html' | 'preview';

// Unlayer editor instance type (accessed via ref.current.editor)
type UnlayerEditor = NonNullable<EditorRef['editor']>;

const STATUS_VARIANT: Record<string, 'default' | 'warning' | 'success' | 'danger'> = {
  draft: 'warning',
  active: 'success',
  archived: 'danger',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extract all {{variable}} tokens (including dot-notation) from an HTML string */
function extractVars(html: string): string[] {
  const matches = new Set<string>();
  for (const m of html.matchAll(/\{\{([\w.]+)\}\}/g)) {
    matches.add(m[1]);
  }
  return [...matches];
}

/** Replace {{var}} / {{group.var}} with values, leaving unmatched tokens as-is */
function applyVars(html: string, values: Record<string, string>): string {
  return html.replace(/\{\{([\w.]+)\}\}/g, (_, key) => values[key] ?? `{{${key}}}`);
}

/** Strip HTML tags to produce a plain-text fallback */
function htmlToText(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
        active ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800',
      )}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

// ─── Merge Tags reference panel ───────────────────────────────────────────────

function MergeTagsPanel({ onClose }: { onClose: () => void }) {
  // Which groups are expanded — all open by default
  const groupKeys = Object.keys(UNLAYER_MERGE_TAGS) as Array<keyof typeof UNLAYER_MERGE_TAGS>;
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groupKeys.map((k) => [k, true])),
  );
  // copiedKey tracks which token was just copied (for checkmark feedback)
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  function toggleGroup(key: string) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function copyToken(token: string) {
    navigator.clipboard.writeText(token).then(() => {
      setCopiedKey(token);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  }

  return (
    <div className="flex flex-col w-64 flex-shrink-0 bg-white border-l border-zinc-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 h-10 border-b border-zinc-100 flex-shrink-0">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700">
          <Variable size={13} className="text-emerald-500" />
          Merge Tags
        </span>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors"
          aria-label="Close merge tags panel"
        >
          <X size={13} />
        </button>
      </div>

      {/* Hint */}
      <p className="px-3 py-2 text-[11px] text-zinc-400 border-b border-zinc-100 leading-relaxed flex-shrink-0">
        Click <Copy size={10} className="inline mx-0.5 relative -top-px" /> to copy a token, then
        paste it into any text block.
      </p>

      {/* Groups */}
      <div className="flex-1 overflow-y-auto">
        {groupKeys.map((groupKey) => {
          const group = UNLAYER_MERGE_TAGS[groupKey];
          const isOpen = expanded[groupKey] ?? true;
          const tags = Object.entries(group.mergeTags) as Array<
            [string, { name: string; value: string; sample: string }]
          >;

          return (
            <div key={groupKey} className="border-b border-zinc-100 last:border-0">
              {/* Group header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className="flex items-center justify-between w-full px-3 py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wide hover:bg-zinc-50 transition-colors"
              >
                <span>{group.name}</span>
                {isOpen ? (
                  <ChevronUp size={11} className="text-zinc-400" />
                ) : (
                  <ChevronDown size={11} className="text-zinc-400" />
                )}
              </button>

              {/* Tags list */}
              {isOpen && (
                <div className="pb-1">
                  {tags.map(([tagKey, tag]) => {
                    const isCopied = copiedKey === tag.value;
                    return (
                      <div
                        key={tagKey}
                        className="group flex items-start justify-between gap-2 px-3 py-1.5 hover:bg-zinc-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-zinc-700 font-medium leading-tight truncate">
                            {tag.name}
                          </div>
                          <div className="text-[10px] font-mono text-emerald-600 truncate mt-0.5">
                            {tag.value}
                          </div>
                          <div className="text-[10px] text-zinc-400 truncate">
                            e.g. {tag.sample}
                          </div>
                        </div>
                        <button
                          onClick={() => copyToken(tag.value)}
                          className={cn(
                            'flex-shrink-0 mt-0.5 p-1 rounded transition-colors',
                            isCopied
                              ? 'text-emerald-500'
                              : 'text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 opacity-0 group-hover:opacity-100',
                          )}
                          title={isCopied ? 'Copied!' : `Copy ${tag.value}`}
                        >
                          {isCopied ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Variable panel (preview tab only) ───────────────────────────────────────

function VariablePanel({
  vars,
  values,
  onChange,
}: {
  vars: string[];
  values: Record<string, string>;
  onChange: (key: string, val: string) => void;
}) {
  const [open, setOpen] = useState(true);

  if (vars.length === 0) return null;

  return (
    <div className="absolute bottom-4 right-4 z-10 w-64 bg-white border border-zinc-200 rounded-xl shadow-lg overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-zinc-600 bg-zinc-50 border-b border-zinc-100 hover:bg-zinc-100 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Variable size={12} />
          Preview values ({vars.length})
        </span>
        {open ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
      </button>
      {open && (
        <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
          {vars.map((v) => (
            <div key={v}>
              <label className="block text-[10px] font-mono text-zinc-400 mb-0.5">
                {`{{${v}}}`}
              </label>
              <input
                type="text"
                placeholder={v}
                value={values[v] ?? ''}
                onChange={(e) => onChange(v, e.target.value)}
                className="w-full px-2 py-1 text-xs border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function TemplateBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  // ── State ──────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<Tab>('visual');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [designJson, setDesignJson] = useState<Record<string, unknown> | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [unlayerReady, setUnlayerReady] = useState(false);
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const [showVisualWarning, setShowVisualWarning] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showMergeTags, setShowMergeTags] = useState(false);
  // Media picker — bridges between Unlayer's registerCallback('image') and React modal
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  // Shortlink picker
  const [shortlinkPickerOpen, setShortlinkPickerOpen] = useState(false);
  const unlayerImageDoneRef = useRef<((res: { progress: number; url?: string }) => void) | null>(
    null,
  );
  const htmlEditedManually = useRef(false);

  const emailEditorRef = useRef<EditorRef>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const monacoEditorRef = useRef<any>(null);
  const [visualWrapperEl, setVisualWrapperEl] = useState<HTMLDivElement | null>(null);
  const editorHeight = useElementHeight(visualWrapperEl);

  // Shorthand to get the Unlayer editor instance
  const getEditor = (): UnlayerEditor | null => emailEditorRef.current?.editor ?? null;

  // ── Fetch template ─────────────────────────────────────────────────────────
  const {
    data: template,
    isLoading,
    isError,
  } = useQuery<Template>({
    queryKey: ['template', id],
    queryFn: () => api.get(`/api/v1/templates/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  // ── Fetch integrations (to check if Lygo is configured) ────────────────────
  const { data: integrations } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get('/api/v1/integrations').then((r) => r.data),
  });
  const lygoConfigured = !!(integrations as Record<string, unknown>)?.lygo;

  // Populate local state when template loads
  useEffect(() => {
    if (!template) return;
    setSubject(template.subject ?? '');
    setHtmlBody(template.bodyHtml ?? '');
    setDesignJson(template.bodyJson ?? null);
    // Pre-populate preview variable panel with catalog sample values
    setVarValues(MERGE_TAG_SAMPLES);
  }, [template]);

  // Load design into Unlayer when it becomes ready
  useEffect(() => {
    if (!unlayerReady) return;
    const editor = getEditor();
    if (!editor) return;
    if (designJson) {
      editor.loadDesign(designJson as Parameters<UnlayerEditor['loadDesign']>[0]);
    } else {
      // No saved design — offer template picker
      setShowTemplatePicker(true);
    }
  }, [unlayerReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register Unlayer callbacks once the editor is ready
  useEffect(() => {
    if (!unlayerReady) return;
    const editor = getEditor();
    if (!editor) return;

    // Listen for design changes
    editor.addEventListener('design:updated', () => {
      setIsDirty(true);
    });

    // Register image upload handler — opens Media Library picker
    editor.registerCallback(
      'image',
      (_file: unknown, done: (res: { progress: number; url?: string }) => void) => {
        // Store the done callback so the modal can resolve it
        unlayerImageDoneRef.current = done;
        setShowMediaPicker(true);
      },
    );
  }, [unlayerReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Computed vars for preview ──────────────────────────────────────────────
  const detectedVars = extractVars(htmlBody);
  const renderedPreview = applyVars(htmlBody, varValues);

  // ── Save handler ──────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (payload: {
      subject?: string;
      bodyHtml?: string;
      bodyText?: string;
      bodyJson?: Record<string, unknown> | null;
      variables?: string[];
    }) => api.patch(`/api/v1/templates/${id}`, payload).then((r) => r.data),
    onSuccess: () => {
      setIsDirty(false);
      setIsSaving(false);
      qc.invalidateQueries({ queryKey: ['template', id] });
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template saved');
    },
    onError: (err) => {
      setIsSaving(false);
      toast.error(extractError(err));
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/templates/${id}/activate`, {}).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['template', id] });
      toast.success('Template activated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function handleSave() {
    setIsSaving(true);
    const editor = getEditor();
    if (tab === 'visual' && editor && unlayerReady) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      editor.exportHtml((data: any) => {
        const html = data.html as string;
        const design = data.design as Record<string, unknown>;
        const plainText = htmlToText(html);
        const vars = extractVars(html);
        setHtmlBody(html);
        setDesignJson(design);
        saveMutation.mutate({
          subject,
          bodyHtml: html,
          bodyText: plainText,
          bodyJson: design,
          variables: vars,
        });
      });
    } else {
      const plainText = htmlToText(htmlBody);
      const vars = extractVars(htmlBody);
      saveMutation.mutate({
        subject,
        bodyHtml: htmlBody,
        bodyText: plainText,
        bodyJson: designJson,
        variables: vars,
      });
    }
  }

  // ── Tab switch handlers ────────────────────────────────────────────────────

  function switchTab(next: Tab) {
    if (next === tab) return;

    // Visual → other: export current design to HTML first
    if (tab === 'visual' && next !== 'visual') {
      const editor = getEditor();
      if (editor && unlayerReady) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        editor.exportHtml((data: any) => {
          setHtmlBody(data.html as string);
          setDesignJson(data.design as Record<string, unknown>);
          setTab(next);
        });
        return;
      }
    }

    // HTML → Visual: warn if edits were made manually
    if (next === 'visual' && htmlEditedManually.current) {
      setShowVisualWarning(true);
      return;
    }

    setTab(next);
  }

  function confirmSwitchToVisual() {
    setShowVisualWarning(false);
    htmlEditedManually.current = false;
    const editor = getEditor();
    if (editor && designJson) {
      editor.loadDesign(designJson as Parameters<UnlayerEditor['loadDesign']>[0]);
    }
    setTab('visual');
  }

  // ── Shortlink selection handler ────────────────────────────────────────────
  function handleShortlinkSelect(shortUrl: string) {
    if (tab === 'html') {
      const editor = monacoEditorRef.current;
      if (editor) {
        const selection = editor.getSelection();
        if (selection) {
          editor.executeEdits('insert-shortlink', [
            { range: selection, text: shortUrl, forceMoveMarkers: true },
          ]);
          editor.focus();
        }
      }
    } else {
      navigator.clipboard.writeText(shortUrl).catch(() => {});
      toast.info('URL copiato negli appunti — incollalo nel link desiderato');
    }
  }

  // ── Unlayer onReady callback ───────────────────────────────────────────────
  const onEditorReady: EmailEditorProps['onReady'] = useCallback(() => {
    setUnlayerReady(true);
  }, []);

  // ── Template picker handler ────────────────────────────────────────────────
  function handleTemplatePick(design: Record<string, unknown> | null) {
    const editor = getEditor();
    if (!editor) return;
    if (design) {
      editor.loadDesign(design as Parameters<UnlayerEditor['loadDesign']>[0]);
      setDesignJson(design);
      setIsDirty(true);
    }
    // null = blank, do nothing (empty canvas is the default)
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-zinc-50">
        <AlertTriangle size={24} className="text-red-400" />
        <p className="text-sm text-zinc-600">Template not found.</p>
        <Button size="sm" onClick={() => navigate('/templates')}>
          Back to templates
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-zinc-50 overflow-hidden">
      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <header className="flex-shrink-0 bg-white border-b border-zinc-200 flex items-center gap-3 px-4 h-12">
        {/* Back */}
        <button
          onClick={() => navigate(`/templates/${id}`)}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors mr-1"
        >
          <ChevronLeft size={16} />
          Back
        </button>

        {/* Template name + status */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-semibold text-zinc-900 truncate">{template.name}</span>
          <Badge variant={STATUS_VARIANT[template.status] ?? 'default'}>{template.status}</Badge>
          {isDirty && <span className="text-[10px] text-zinc-400">· unsaved changes</span>}
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-0.5 bg-zinc-100 rounded-lg p-1">
          <TabButton
            active={tab === 'visual'}
            onClick={() => switchTab('visual')}
            icon={LayoutTemplate}
            label="Visual"
          />
          <TabButton
            active={tab === 'html'}
            onClick={() => switchTab('html')}
            icon={Code2}
            label="HTML"
          />
          <TabButton
            active={tab === 'preview'}
            onClick={() => switchTab('preview')}
            icon={Eye}
            label="Preview"
          />
        </div>

        {/* Template picker button — only visible on visual tab */}
        {tab === 'visual' && (
          <button
            onClick={() => setShowTemplatePicker(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-colors border border-zinc-200 hover:border-zinc-300"
            title="Browse starting templates"
          >
            <LayoutTemplate size={13} />
            Templates
          </button>
        )}

        {/* Merge tags toggle */}
        <button
          onClick={() => setShowMergeTags((v) => !v)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border',
            showMergeTags
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 border-zinc-200 hover:border-zinc-300',
          )}
          title="Toggle merge tags reference panel"
        >
          <Variable size={13} />
          Variables
        </button>

        {/* Shortlink picker button */}
        {lygoConfigured && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShortlinkPickerOpen(true)}
            disabled={tab === 'preview'}
            title="Inserisci shortlink"
          >
            <Link2 className="w-4 h-4 mr-1.5" />
            Shortlink
          </Button>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {template.status !== 'active' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
            >
              <CheckCircle size={13} />
              {activateMutation.isPending ? 'Activating…' : 'Activate'}
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={isSaving || saveMutation.isPending}>
            {isSaving || saveMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Save size={13} />
            )}
            Save
          </Button>
        </div>
      </header>

      {/* ── Subject bar (email only) ──────────────────────────────────────────── */}
      {template.channel === 'email' && (
        <div className="flex-shrink-0 bg-white border-b border-zinc-100 flex items-center gap-3 px-4 py-2">
          <span className="text-xs font-medium text-zinc-400 w-14 flex-shrink-0">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setIsDirty(true);
            }}
            placeholder="Email subject line…"
            className="flex-1 text-sm text-zinc-800 bg-transparent border-none outline-none placeholder:text-zinc-300"
          />
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* ── Editor area ──────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 relative">
          {/* ── Visual tab (Unlayer) ─────────────────────────────────────── */}
          <div
            ref={setVisualWrapperEl}
            className={cn('absolute inset-0', tab === 'visual' ? 'z-10' : 'invisible -z-10')}
          >
            {/* Spinner: while wrapper height not yet measured OR Unlayer not ready */}
            {(editorHeight === 0 || !unlayerReady) && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-50 z-20">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 size={24} className="animate-spin text-emerald-400" />
                  <p className="text-sm text-zinc-400">Loading visual editor…</p>
                </div>
              </div>
            )}
            {/* Mount EmailEditor only once we have a real pixel height —
                prevents Unlayer from laying out its iframe at height 0 */}
            {editorHeight > 0 && (
              <EmailEditor
                ref={emailEditorRef}
                onReady={onEditorReady}
                minHeight={editorHeight}
                style={{ height: editorHeight, display: 'block' }}
                options={
                  {
                    displayMode: 'email',
                    appearance: {
                      theme: {
                        name: 'map-brand',
                        extends: 'modern_light',
                        isDark: false,
                        mapping: {
                          colors: {
                            // ── Primary scale → MAP Emerald ──────────────────────
                            primary_01: '#ecfdf5',
                            primary_02: '#d1fae5',
                            primary_03: '#a7f3d0',
                            primary_04: '#6ee7b7',
                            primary_05: '#34d399',
                            primary_06: '#10b981',
                            primary_07: '#059669',
                            primary_08: '#047857',
                            primary_09: '#065f46',
                            primary_10: '#064e3b',
                            primary_11: '#022c22',
                            // ── Neutral scale → MAP Zinc ─────────────────────────
                            black_00: '#18181b',
                            black_01: '#27272a',
                            black_02: '#3f3f46',
                            black_03: '#52525b',
                            black_04: '#71717a',
                            black_05: '#a1a1aa',
                            black_06: '#d4d4d8',
                            black_07: '#e4e4e7',
                            black_08: '#f4f4f5',
                            black_09: '#fafafa',
                            black_10: '#ffffff',
                          },
                        },
                      },
                      panels: {
                        tools: { dock: 'left' },
                      },
                    },
                    features: {
                      imageEditor: true,
                      colorPicker: {
                        colors: [
                          {
                            id: 'brand_colors',
                            label: 'MAP Brand',
                            colors: [
                              // Emerald brand scale
                              '#10b981',
                              '#059669',
                              '#047857',
                              '#065f46',
                              // Zinc neutrals
                              '#18181b',
                              '#3f3f46',
                              '#71717a',
                              '#a1a1aa',
                              '#e4e4e7',
                              '#fafafa',
                              // Semantic
                              '#f59e0b',
                              '#ef4444',
                              '#0ea5e9',
                              '#ffffff',
                            ],
                          },
                        ],
                      },
                    },
                    customCSS: [
                      // Selected block outline → emerald
                      '.blockbuilder-layer-selector .is-selected { border-color: #10b981 !important; outline-color: #10b981 !important; }',
                      // Drag-over highlight → emerald tint
                      '.blockbuilder-drop-area.is-dragging-over { border-color: #10b981 !important; background: #ecfdf5 !important; }',
                    ],
                    fonts: { showDefaultFonts: true },
                    mergeTags: UNLAYER_MERGE_TAGS,
                  } as object
                }
              />
            )}
          </div>

          {/* ── HTML tab (Monaco) ────────────────────────────────────────── */}
          <div className={cn('absolute inset-0', tab === 'html' ? 'z-10' : 'invisible -z-10')}>
            <Editor
              height="100%"
              language="html"
              value={htmlBody}
              theme="vs-light"
              onMount={(editor) => {
                monacoEditorRef.current = editor;
              }}
              onChange={(val) => {
                setHtmlBody(val ?? '');
                setIsDirty(true);
                htmlEditedManually.current = true;
              }}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                wordWrap: 'on',
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                formatOnPaste: true,
              }}
            />
          </div>

          {/* ── Preview tab ─────────────────────────────────────────────── */}
          <div
            className={cn(
              'absolute inset-0 bg-zinc-100',
              tab === 'preview' ? 'z-10' : 'invisible -z-10',
            )}
          >
            {htmlBody ? (
              <>
                <iframe
                  srcDoc={renderedPreview}
                  title="Email preview"
                  className="w-full h-full border-none"
                  sandbox="allow-same-origin"
                />
                <VariablePanel
                  vars={detectedVars}
                  values={varValues}
                  onChange={(k, v) => setVarValues((prev) => ({ ...prev, [k]: v }))}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-zinc-400">
                <Eye size={32} className="opacity-30" />
                <p className="text-sm">No HTML content yet.</p>
                <p className="text-xs">Build your email in the Visual or HTML tab first.</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Merge Tags panel (right sidebar) ─────────────────────────────── */}
        {showMergeTags && <MergeTagsPanel onClose={() => setShowMergeTags(false)} />}
      </div>

      {/* ── Template picker modal ─────────────────────────────────────────────── */}
      <TemplatePickerModal
        open={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handleTemplatePick}
      />

      {/* ── Media Library picker (opened by Unlayer image callback) ──────────── */}
      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => {
          setShowMediaPicker(false);
          // Cancel the pending Unlayer image insertion
          if (unlayerImageDoneRef.current) {
            unlayerImageDoneRef.current({ progress: 0 });
            unlayerImageDoneRef.current = null;
          }
        }}
        onSelect={(url) => {
          setShowMediaPicker(false);
          if (unlayerImageDoneRef.current) {
            unlayerImageDoneRef.current({ progress: 100, url });
            unlayerImageDoneRef.current = null;
          }
        }}
      />

      {/* ── Shortlink picker modal ────────────────────────────────────────────── */}
      <ShortlinkPickerModal
        isOpen={shortlinkPickerOpen}
        onClose={() => setShortlinkPickerOpen(false)}
        onSelect={handleShortlinkSelect}
      />

      {/* ── Switch-to-visual warning modal ───────────────────────────────────── */}
      {showVisualWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-zinc-900">Switch to Visual editor?</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Manual HTML edits may not be reflected in the visual design. The editor will
                  reload from the last saved design JSON.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setShowVisualWarning(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={confirmSwitchToVisual}>
                Switch anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
